import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';

import { ServerErrorRetryService } from '../services/server-error-retry.service';

// Opt-out for best-effort lookups (e.g. the OCW mileage): a 5xx propagates to the
// caller's own fallback instead of opening the blocking retry modal.
export const SKIP_SERVER_ERROR_RETRY = new HttpContextToken<boolean>((): boolean => false);

/** The class of failures the retry modal owns: the backend is down or unhealthy. */
export function isServerError(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status >= 500;
}

/**
 * Every 5xx response opens the blocking server-error modal and is retried once,
 * transparently, when the shared countdown elapses (ServerErrorRetryService).
 * After a failed retry the cycle is over: further 5xx just propagate.
 */
export const apiErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const serverErrorRetry = inject(ServerErrorRetryService);

  return next(req).pipe(
    catchError((errorResponse: unknown): Observable<HttpEvent<unknown>> => {
      if (
        !isServerError(errorResponse) ||
        req.context.get(SKIP_SERVER_ERROR_RETRY) ||
        serverErrorRetry.isFailed()
      ) {
        return throwError((): unknown => errorResponse);
      }

      console.error('Server error', errorResponse);
      return serverErrorRetry.retryAfterCountdown(
        (): Observable<HttpEvent<unknown>> => next(req),
        errorResponse,
      );
    }),
  );
};
